import CheckIcon from '@mui/icons-material/Check';
import type { SelectChangeEvent, SxProps, TypographyProps } from '@mui/material';
import {
    Box,
    Checkbox,
    FormControlLabel,
    ListItemText,
    MenuItem,
    Select,
    Stack,
    styled,
    Switch,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
} from '@mui/material';
import type { MouseEvent, SyntheticEvent } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { TextOnlyTooltip } from '@/src/components/Tooltip/TextOnlyTooltip';
import { smallScreenTab } from '@/src/shared/constants/MediaQueryConstants';
import type { ChatMetadata } from '@/src/shared/types';
import { chatActions, selectChatMetadata } from '@/src/store/apps/chat';
import {
    hoverLightPaleOrange,
    lightOrange,
    primaryOrange,
    warningTextColor,
} from '@/src/styles/theme/lightThemeOptions';

import type { UserSelectOptionsType } from '../../knowledge-base/ContentSource/constants';
import {
    DEEP_THINK_ALLOWED_OPTIONS,
    UserSelectionOptions,
} from '../../knowledge-base/ContentSource/constants';
import { ParagraphStyle } from './styles';

const StyledStack = styled(Stack)({
    '& .MuiFormControlLabel-root': {
        height: '24px',
        gap: '4px',
        '@media(max-width: 767px)': {
            width: '80px',
        },
        '& .MuiTypography-root': {
            fontWeight: '500',
            fontSize: '12px',
            lineHeight: '16px',
        },
    },
    '@media(max-width: 767px)': {
        '& .MuiSelect-select': {
            padding: '8px 14px',
        },
    },
    '@media(max-width: 1024px)': {
        '& .MuiToggleButton-root': {
            padding: '10px 8px',
        },
    },
});

const InputSelectSx: SxProps = {
    ...ParagraphStyle,
    borderRadius: '4px',
    left: '16px',
    top: '2px',
};

const DeepThinkSwitchSx: SxProps = {
    mt: 2,
    ml: 2,
};

const StyledToggleButton = styled(ToggleButton)(() => ({
    textTransform: 'none',
    padding: '8px 14px',
    color: warningTextColor,
    '&.Mui-selected': {
        backgroundColor: lightOrange,
        color: primaryOrange,
    },
    '&:hover': {
        backgroundColor: hoverLightPaleOrange,
    },
}));

export interface AdvancedFeatureListProps {
    selectedOption: UserSelectOptionsType;
    handleSelectedOptionChange: (option: UserSelectOptionsType) => void;
}

export const AdvancedFeatureList = ({
    selectedOption,
    handleSelectedOptionChange,
}: AdvancedFeatureListProps) => {
    const { t } = useTranslation('ginger');
    const dispatch = useDispatch();
    const chatMetadata = useSelector(selectChatMetadata);
    const updateChatMetadataField = useCallback(
        (field: keyof ChatMetadata, value: boolean) => {
            dispatch(chatActions.updateChatMetadata({ [field]: value }));
        },
        [dispatch]
    );

    const switchDeepThink = useCallback(
        (event: SyntheticEvent, checked: boolean) => updateChatMetadataField('deep_think', checked),
        [updateChatMetadataField]
    );

    const resetDeepThinkAndUpdate = useCallback(
        (option: UserSelectOptionsType) => {
            updateChatMetadataField('deep_think', false);
            handleSelectedOptionChange(option);
        },
        [updateChatMetadataField, handleSelectedOptionChange]
    );

    const handleSelectChange = (event: SelectChangeEvent<string>) => {
        const newSelectedOption = event.target.value as UserSelectOptionsType;
        resetDeepThinkAndUpdate(newSelectedOption);
    };

    const handleToggleChange = (
        event: MouseEvent<HTMLElement>,
        newSelectedOption: UserSelectOptionsType | null
    ) => {
        if (!newSelectedOption) return;
        resetDeepThinkAndUpdate(newSelectedOption);
    };

    const isDeepThinkAllowed = DEEP_THINK_ALLOWED_OPTIONS.includes(selectedOption);
    const isMobileScreen: boolean = useMediaQuery(smallScreenTab);
    const [isSelectOpen, setIsSelectOpen] = useState(false);

    return (
        <StyledStack direction="row" alignItems="center" gap={isMobileScreen ? '12px' : 0}>
            {!isMobileScreen ? (
                <>
                    <ToggleButtonGroup
                        className="chat-buttons"
                        value={selectedOption}
                        exclusive
                        onChange={handleToggleChange}
                        sx={{ ml: 2, mt: 2 }}
                    >
                        {UserSelectionOptions.map((option) => {
                            const selected = selectedOption === option.value;

                            return (
                                <StyledToggleButton key={option.value} value={option.value}>
                                    <TextOnlyTooltip
                                        title={t(option.description)}
                                        placement="bottom"
                                    >
                                        <Typography
                                            sx={{
                                                ...ParagraphStyle,
                                                color: selected ? primaryOrange : warningTextColor,
                                            }}
                                        >
                                            {t(option.label)}
                                        </Typography>
                                    </TextOnlyTooltip>
                                </StyledToggleButton>
                            );
                        })}
                    </ToggleButtonGroup>
                    {isDeepThinkAllowed ? (
                        <FormControlLabel
                            sx={DeepThinkSwitchSx}
                            className="deep-think"
                            labelPlacement="start"
                            control={<Switch size="small" checked={chatMetadata.deep_think} />}
                            label={t('deep_reasoning')}
                            onChange={switchDeepThink}
                        />
                    ) : (
                        <TextOnlyTooltip title={t('deep_reasoning_tooltip')} placement="bottom">
                            <FormControlLabel
                                sx={DeepThinkSwitchSx}
                                labelPlacement="start"
                                control={
                                    <Switch
                                        size="small"
                                        checked={chatMetadata.deep_think}
                                        disabled
                                    />
                                }
                                label={t('deep_reasoning')}
                            />
                        </TextOnlyTooltip>
                    )}
                </>
            ) : (
                <>
                    <Select
                        className="chat-buttons"
                        sx={{
                            ...InputSelectSx,
                            ...(isSelectOpen && {
                                backgroundColor: lightOrange,
                            }),
                        }}
                        value={selectedOption}
                        onChange={handleSelectChange}
                        onOpen={() => setIsSelectOpen(true)}
                        onClose={() => setIsSelectOpen(false)}
                        renderValue={(selected) => {
                            const selectedValue = UserSelectionOptions.find(
                                (option) => option.value === selected
                            );
                            return selectedValue ? t(selectedValue.label) : '';
                        }}
                        MenuProps={{
                            anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left',
                            },
                            transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left',
                            },
                            PaperProps: {
                                sx: {
                                    mt: 0.2,
                                    borderRadius: 0.5,
                                    boxShadow: 3,
                                },
                            },
                        }}
                    >
                        {UserSelectionOptions.map((option) => {
                            const selected = selectedOption === option.value;

                            return (
                                <MenuItem key={option.value} value={option.value}>
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        width="100%"
                                        gap="16px"
                                    >
                                        <ListItemText
                                            primary={t(option.label)}
                                            secondary={t(option.description)}
                                            primaryTypographyProps={{
                                                ...(ParagraphStyle as TypographyProps),

                                                color: selected ? primaryOrange : warningTextColor,
                                            }}
                                            secondaryTypographyProps={{
                                                color: 'text.secondary',
                                            }}
                                        />

                                        {selected && <CheckIcon fontSize="small" color="primary" />}
                                    </Box>
                                </MenuItem>
                            );
                        })}
                    </Select>

                    {isDeepThinkAllowed && (
                        <FormControlLabel
                            className="deep-think"
                            labelPlacement="start"
                            control={<Checkbox size="small" checked={chatMetadata.deep_think} />}
                            label={t('deep_reasoning')}
                            onChange={switchDeepThink}
                        />
                    )}
                </>
            )}
        </StyledStack>
    );
};
